import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  GraduationCap,
  Play,
  Clock,
  Award,
  CheckCircle,
  Star,
  ChevronRight,
  Filter,
  BookOpen,
  Target,
} from "lucide-react";
import { trainingAPI } from "../api";
import clsx from "clsx";
import toast from "react-hot-toast";

const difficultyConfig = {
  Beginner: { color: "bg-green-100 text-green-800" },
  Intermediate: { color: "bg-blue-100 text-blue-800" },
  Advanced: { color: "bg-yellow-100 text-yellow-800" },
  Expert: { color: "bg-red-100 text-red-800" },
};

const ModuleCard = ({ module, onStart }) => {
  const difficulty =
    difficultyConfig[module.difficulty_level] || difficultyConfig.Beginner;
  const progress = module.user_progress || 0;
  const isCompleted = progress === 100;
  const isStarted = progress > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className={clsx(
              "w-12 h-12 rounded-xl flex items-center justify-center",
              isCompleted ? "bg-green-100" : "bg-blue-100"
            )}
          >
            {isCompleted ? (
              <Award className="w-6 h-6 text-green-500" />
            ) : (
              <GraduationCap className="w-6 h-6 text-blue-500" />
            )}
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">{module.title}</h3>
            <p className="text-sm text-slate-500">
              {module.machine_name || "General"}
            </p>
          </div>
        </div>
        <span className={clsx("badge", difficulty.color)}>
          {module.difficulty_level}
        </span>
      </div>

      {module.description && (
        <p className="text-sm text-slate-500 line-clamp-2 mb-4">
          {module.description}
        </p>
      )}

      <div className="flex items-center gap-4 mb-4 text-sm text-slate-500">
        <div className="flex items-center gap-1">
          <Clock className="w-4 h-4" />
          <span>{module.estimated_duration || 30} min</span>
        </div>
        <div className="flex items-center gap-1">
          <BookOpen className="w-4 h-4" />
          <span>{module.category || "General"}</span>
        </div>
      </div>

      {/* Progress Bar */}
      {isStarted && (
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-slate-500">Progress</span>
            <span className="text-slate-800 font-medium">{progress}%</span>
          </div>
          <div className="w-full h-2 bg-slate-200 rounded-full">
            <div
              className={clsx(
                "h-full rounded-full transition-all",
                isCompleted ? "bg-green-500" : "bg-blue-500"
              )}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-slate-200">
        {isCompleted ? (
          <span className="badge bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3 mr-1" />
            Completed
          </span>
        ) : (
          <button
            onClick={() => onStart(module.id)}
            className="btn btn-primary btn-sm"
          >
            <Play className="w-3 h-3" />
            {isStarted ? "Continue" : "Start"}
          </button>
        )}
        <ChevronRight className="w-5 h-5 text-slate-400" />
      </div>
    </motion.div>
  );
};

const Training = () => {
  const [categoryFilter, setCategoryFilter] = useState("");
  const [difficultyFilter, setDifficultyFilter] = useState("");
  const queryClient = useQueryClient();

  const { data: modulesData, isLoading } = useQuery({
    queryKey: [
      "training-modules",
      { category: categoryFilter, difficulty: difficultyFilter },
    ],
    queryFn: () =>
      trainingAPI
        .getModules({
          category: categoryFilter || undefined,
          difficulty: difficultyFilter || undefined,
        })
        .then((res) => res.data),
  });

  const { data: certifications } = useQuery({
    queryKey: ["certifications"],
    queryFn: () => trainingAPI.getCertifications().then((res) => res.data.data),
  });

  const { data: stats } = useQuery({
    queryKey: ["training-stats"],
    queryFn: () => trainingAPI.getStats().then((res) => res.data.data),
  });

  const startMutation = useMutation({
    mutationFn: (id) => trainingAPI.start(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["training-modules"] });
      toast.success("Training started!");
    },
  });

  const modules = modulesData?.data || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Training Center</h1>
          <p className="text-slate-500">
            Learn and get certified on machine operations
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-slate-200 rounded-xl p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 mb-1">Modules Started</p>
              <p className="text-2xl font-bold text-slate-800">
                {stats?.modules_started || 0}
              </p>
            </div>
            <BookOpen className="w-8 h-8 text-blue-500 opacity-50" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white border border-slate-200 rounded-xl p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 mb-1">Certified</p>
              <p className="text-2xl font-bold text-green-500">
                {stats?.modules_certified || 0}
              </p>
            </div>
            <Award className="w-8 h-8 text-green-500 opacity-50" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white border border-slate-200 rounded-xl p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 mb-1">Avg Score</p>
              <p className="text-2xl font-bold text-slate-800">
                {stats?.avg_score ? Math.round(stats.avg_score) : "--"}%
              </p>
            </div>
            <Target className="w-8 h-8 text-yellow-500 opacity-50" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white border border-slate-200 rounded-xl p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 mb-1">Training Hours</p>
              <p className="text-2xl font-bold text-slate-800">
                {stats?.total_training_minutes
                  ? Math.round(stats.total_training_minutes / 60)
                  : 0}
                h
              </p>
            </div>
            <Clock className="w-8 h-8 text-cyan-500 opacity-50" />
          </div>
        </motion.div>
      </div>

      {/* Certifications */}
      {certifications && certifications.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-yellow-500" />
            Your Certifications
          </h3>
          <div className="flex flex-wrap gap-3">
            {certifications.map((cert) => (
              <div
                key={cert.id}
                className="flex items-center gap-2 px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-xl"
              >
                <Star className="w-4 h-4 text-yellow-500" />
                <span className="text-yellow-900 font-medium">
                  {cert.title}
                </span>
                <span className="text-xs text-yellow-700">
                  Score: {cert.score}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4">
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="input w-auto"
        >
          <option value="">All Categories</option>
          <option value="Safety">Safety</option>
          <option value="Technical">Technical</option>
          <option value="Operations">Operations</option>
          <option value="Maintenance">Maintenance</option>
        </select>
        <select
          value={difficultyFilter}
          onChange={(e) => setDifficultyFilter(e.target.value)}
          className="input w-auto"
        >
          <option value="">All Levels</option>
          <option value="Beginner">Beginner</option>
          <option value="Intermediate">Intermediate</option>
          <option value="Advanced">Advanced</option>
        </select>
      </div>

      {/* Modules Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="bg-white border border-slate-200 rounded-xl p-5"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl skeleton" />
                <div className="flex-1">
                  <div className="h-5 w-32 skeleton mb-2" />
                  <div className="h-4 w-24 skeleton" />
                </div>
              </div>
              <div className="h-12 skeleton mb-4" />
              <div className="h-2 skeleton rounded-full" />
            </div>
          ))}
        </div>
      ) : modules.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {modules.map((module) => (
            <ModuleCard
              key={module.id}
              module={module}
              onStart={(id) => startMutation.mutate(id)}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <GraduationCap className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-800 mb-2">
            No training modules
          </h3>
          <p className="text-slate-500">
            Training modules will appear here when available.
          </p>
        </div>
      )}
    </div>
  );
};

export default Training;
